import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Moon, Activity, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';

// Correctif : ProtectedRoute (state.from = objet location) et DoctorProfile
// (state.from = chaine de chemin) redirigeaient tous les deux vers /login
// avec un "from" a reprendre apres connexion, mais ce champ n'etait jamais
// lu ici — un patient bloque en plein milieu d'une reservation (ou renvoye
// depuis une page protegee) atterrissait systematiquement sur le tableau de
// bord par defaut, perdant totalement son intention initiale. On n'accepte
// qu'un chemin interne commencant par "/" (jamais une URL absolue ou
// "//hote" venant de cet etat) pour eviter une redirection ouverte.
const safeInternalPath = (from) => {
  let path = null;
  if (typeof from === 'string') path = from;
  else if (from && typeof from === 'object' && typeof from.pathname === 'string') {
    path = from.pathname + (from.search || '') + (from.hash || '');
  }
  if (!path || !path.startsWith('/') || path.startsWith('//')) return null;
  return path;
};

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyMfa, loginWithGoogle } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Double authentification (cahier des charges labo, §32) : une fois le mot
  // de passe accepte, si le compte a la MFA active, on reste sur cette page
  // pour demander le code TOTP avant d'ouvrir la session.
  const [mfaPendingToken, setMfaPendingToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');

  const formatApiErrorDetail = (detail) => {
    if (detail == null) return t('login_error');
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
  };

  // Redirection post-connexion selon le role — inchangee, seulement extraite
  // pour etre appelee aussi bien apres un mot de passe seul qu'apres un
  // mot de passe + code MFA (handleMfaSubmit ci-dessous).
  const redirectForUser = async (user) => {
    const role = user.role || user.user_role;

    if (role === 'admin') {
      navigate('/admin');
    } else if (role === 'doctor') {
      // Un medecin rattache a un hopital travaille dans l'espace hopital.
      let target = '/doctor/dashboard';
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sih/me`, {
          credentials: 'include',
        });
        if (res.ok) {
          const d = await res.json();
          if (d && d.establishment_type === 'hospital') target = '/hopital';
        }
      } catch (e) { /* medecin de cabinet : espace habituel */ }
      navigate(target);
    } else if (role === 'clinic_admin') {
      // Un hopital utilise l'espace SIH riche (/hopital) ; un laboratoire a
      // desormais son propre espace dedie (/laboratoire, voir le plan de
      // reconstruction, Phase 7) ; une clinique/cabinet classique garde son
      // tableau de bord habituel. /api/sih/me resout correctement
      // establishment_type meme pour un labo (fallback proprietaire de
      // get_establishment_context), donc un seul appel suffit ici.
      let target = '/clinic/dashboard';
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sih/me`, {
          credentials: 'include',
        });
        if (res.ok) {
          const d = await res.json();
          if (d && d.establishment_type === 'hospital') target = '/hopital';
          else if (d && d.establishment_type === 'laboratory') target = '/laboratoire';
        }
      } catch (e) { /* clinique classique : espace habituel */ }
      navigate(target);
    } else if (role === 'clinic_staff') {
      // Compte 'Secretaire / Accueil' d'une clinique classique : meme
      // tableau de bord que clinic_admin, avec des onglets limites cote
      // frontend (voir ClinicDashboard.js). Pas de cas hopital/labo/pharmacie
      // possible pour ce role (uniquement cree depuis l'espace clinique
      // classique), donc pas besoin d'appeler /api/sih/me ici.
      navigate('/clinic/dashboard');
    } else if (role === 'establishment_member') {
      // Personnel d'un etablissement : hopital -> /hopital, labo -> /laboratoire,
      // pharmacie -> /officine. Un membre du personnel labo (db.lab_staff) ou
      // le titulaire d'une pharmacie (db.clinics, establishment_type=pharmacy)
      // ne sont PAS resolus par /api/sih/me (qui ne connait que
      // db.hospital_staff) : on tente donc /api/lab/me puis /api/officine/me
      // (get_lab_establishment_context / get_pharmacy_establishment_context
      // renvoient 403 proprement si l'utilisateur n'a aucun rattachement de
      // ce type), et on retombe sur /hopital sinon (comportement historique
      // inchange pour le personnel hopitalier). IMPORTANT : ne jamais laisser
      // un titulaire de pharmacie atterrir sur /hopital — get_establishment_context
      // le traiterait par defaut comme "gestionnaire_hopital" de sa propre
      // fiche (fallback proprietaire, utils.py), un role qui ne lui correspond
      // pas — d'ou l'ordre des essais ci-dessous.
      let target = '/hopital';
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/lab/me`, {
          credentials: 'include',
        });
        if (res.ok) {
          target = '/laboratoire';
        } else {
          const res2 = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/officine/me`, {
            credentials: 'include',
          });
          if (res2.ok) target = '/officine';
        }
      } catch (e) { /* personnel hopitalier : espace habituel */ }
      navigate(target);
    } else {
      // Correctif : un patient bloque en plein reservation (ou renvoye
      // depuis une page protegee) retrouve la page qu'il visait au lieu
      // d'atterrir systematiquement sur son tableau de bord par defaut.
      navigate(safeInternalPath(location.state?.from) || '/patient/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);

      // Double authentification (§32) : mot de passe correct, mais il manque
      // encore le code TOTP. On reste sur cette page pour le demander.
      if (result && result.mfaRequired) {
        setMfaPendingToken(result.pendingToken);
        return;
      }

      toast.success(t('login_success'));

      // S'abonner aux notifications push (best-effort, ne bloque pas)
      try { if (window.tabbibakInitPush) window.tabbibakInitPush(); } catch (e) {}

      await redirectForUser(result);

    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setMfaError('');
    setMfaLoading(true);
    try {
      const user = await verifyMfa(mfaPendingToken, mfaCode);
      toast.success(t('login_success'));
      try { if (window.tabbibakInitPush) window.tabbibakInitPush(); } catch (e) {}
      await redirectForUser(user);
    } catch (err) {
      setMfaError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setMfaLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="flex items-center text-[#0596DE]">
              <Moon className="w-8 h-8" />
              <Activity className="w-8 h-8 -ml-2" />
            </div>
            <span className="font-bold text-2xl text-[#0596DE] font-['Outfit']">Tabbibak.com</span>
          </Link>

          <h1 className="text-3xl font-bold text-[#0F172A] mb-2">
            {t('login_title')}
          </h1>
          <p className="text-[#64748B] mb-8">
            {t('login_subtitle')}
          </p>

          {mfaPendingToken ? (
            <>
              {mfaError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-6">
                  {mfaError}
                </div>
              )}
              <p className="text-[#64748B] mb-6 -mt-4">
                Entrez le code à 6 chiffres de votre application d'authentification (ou un code de secours).
              </p>
              <form onSubmit={handleMfaSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="mfa-code" className="text-[#0F172A]">Code de vérification</Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    placeholder="123456"
                    className="mt-1 rounded-xl text-center tracking-[0.3em] text-lg"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-full bg-[#0596DE] hover:bg-[#047CBA] h-12"
                  disabled={mfaLoading}
                >
                  {mfaLoading ? t('login_loading') : 'Vérifier'}
                </Button>
                <button
                  type="button"
                  onClick={() => { setMfaPendingToken(null); setMfaCode(''); setMfaError(''); }}
                  className="w-full text-center text-sm text-[#64748B] hover:text-[#0F172A]"
                >
                  ← Revenir à la connexion
                </button>
              </form>
            </>
          ) : (
            <>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl mb-6" data-testid="login-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
                <div>
                  <Label htmlFor="email" className="text-[#0F172A]">{t('login_email')}</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="votre@email.com"
                      className="pl-10 rounded-xl"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="text-[#0F172A]">{t('login_password')}</Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      className="pl-10 pr-10 rounded-xl"
                      required
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#64748B] hover:text-[#0F172A]"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full rounded-full bg-[#0596DE] hover:bg-[#047CBA] h-12"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? t('login_loading') : t('login_btn')}
                </Button>
              </form>

              <div className="relative my-8">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#E2E8F0]"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-[#F8FAFC] text-[#64748B]">{t('login_or')}</span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full rounded-full h-12"
                onClick={loginWithGoogle}
                data-testid="google-login-btn"
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('login_google')}
              </Button>
            </>
          )}

          <p className="text-center text-[#64748B] mt-8">
            {t('login_no_account')}{' '}
            <Link to="/register" className="text-[#0596DE] hover:underline font-medium">
              {t('login_register')}
            </Link>
          </p>

          <p className="text-center text-[#64748B] mt-2">
            <Link to="/forgot-password" className="text-[#64748B] hover:text-[#0596DE] hover:underline text-sm">
              {t('login_forgot')}
            </Link>
          </p>

          <p className="text-center text-[#64748B] mt-4">
            <Link to="/doctor/register" className="text-[#0596DE] hover:underline font-medium">
              {t('login_doctor')}
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://customer-assets.emergentagent.com/job_tabbibak-algeria/artifacts/bch5rgxl_radja.jpg"
          alt="Medical clinic"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#0596DE]/20"></div>
      </div>
    </div>
  );
};

export default Login;